import {
  useSortable,
  defaultAnimateLayoutChanges,
  type AnimateLayoutChanges,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

interface ChildrenProps {
  isDragging: boolean;
  isSorting: boolean;
  handleProps?: React.HTMLAttributes<unknown>;
}

interface SortableProps {
  id: string;
  children: ((props: ChildrenProps) => React.ReactNode) | React.ReactElement;
  disabledDrop?: boolean;
  disabledDrag?: boolean;
  disableDropAnimation?: boolean;
  withHandle?: boolean;
  // Custom data that drag event handlers read from `active.data.current`.
  data?: Record<string, unknown>;
}

interface AnimateLayoutChangesOptions {
  disableDropAnimation?: boolean;
}

/**
 * Why:
 * By default, dnd-kit animates items smoothly into their new positions after a drop.
 * However, in some cases (e.g., optimistic updates, when items are immediately refetched or reordered via API),
 * this animation can cause visual glitches or "jumping" because the DOM updates
 * before the animation completes.
 *
 * When `disableDropAnimation` is true:
 * - Returns `false` for the drop moment (`wasDragging` = item was just released)
 * - This makes the item snap instantly to its new position without animation
 * - Other layout changes (reordering during drag) still animate normally
 */
const createAnimateLayoutChanges =
  (options: AnimateLayoutChangesOptions): AnimateLayoutChanges =>
  (args) => {
    if (options.disableDropAnimation && args.wasDragging) {
      return false;
    }
    return defaultAnimateLayoutChanges(args);
  };

export function Sortable(props: SortableProps) {
  const {attributes, listeners, setNodeRef, isDragging, transform, transition, isSorting} =
    useSortable({
      id: props.id,
      data: props.data,
      animateLayoutChanges: createAnimateLayoutChanges({
        disableDropAnimation: props.disableDropAnimation,
      }),
      disabled: {
        droppable: props.disabledDrop ?? false,
        draggable: props.disabledDrag ?? false,
      },
    });

  const style = {
    transition,
    transform: transform ? CSS.Translate.toString(transform) : undefined,
  };

  const dndProps = {
    ...attributes,
    ...listeners,
  };

  return (
    // eslint-disable-next-line react/forbid-dom-props
    <div ref={setNodeRef} style={style} {...(props.withHandle ? undefined : dndProps)}>
      {typeof props.children === 'function'
        ? props.children({
            isDragging,
            isSorting,
            handleProps: props.withHandle ? dndProps : undefined,
          })
        : props.children}
    </div>
  );
}
